import { useEffect, useRef } from "react";
import { Funnel, MagnifyingGlass, X } from "@phosphor-icons/react";
import { DatePicker } from "@/components/ui/date-picker";

// Shared building blocks for list pages (Rezervacije, Računi, Gosti) so they look identical.

export function ListHeader({ title, summary, actions }) {
  return (
    <div className="flex items-end justify-between gap-3" data-testid="list-header">
      <div className="min-w-0">
        <h1 className="hidden font-heading text-2xl font-bold tracking-tight text-foreground sm:block">{title}</h1>
        {summary && <p className="truncate text-xs text-muted-foreground sm:mt-1 sm:text-sm" data-testid="list-summary">{summary}</p>}
      </div>
      {actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
    </div>
  );
}

export function ListToolbar({ value, onChange, onSubmit, placeholder, filtersOpen, onToggleFilters, filterCount = 0, testIdPrefix, children }) {
  // Live search: run the search ~300ms after the user stops typing
  const submitRef = useRef(onSubmit);
  submitRef.current = onSubmit;
  const first = useRef(true);
  useEffect(() => {
    if (first.current) { first.current = false; return undefined; }
    const t = setTimeout(() => submitRef.current?.(), 300);
    return () => clearTimeout(t);
  }, [value]);
  return (
    <div className="flex items-center gap-2" data-testid={`${testIdPrefix}-toolbar`}>
      <div className="relative min-w-0 flex-1">
        <MagnifyingGlass size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && onSubmit?.()}
          placeholder={placeholder}
          enterKeyHint="search"
          className="rw-control !pl-9 !pr-8"
          data-testid={`${testIdPrefix}-search-input`}
        />
        {value && (
          <button onClick={() => { onChange(""); }} className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted-foreground hover:text-foreground" aria-label="Očisti pretragu" data-testid={`${testIdPrefix}-search-clear`}>
            <X size={12} />
          </button>
        )}
      </div>
      <button
        onClick={onToggleFilters}
        className={`relative inline-flex h-10 shrink-0 items-center gap-2 rounded-md border px-3 text-sm font-medium transition-colors ${filtersOpen ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-foreground hover:bg-muted"}`}
        aria-expanded={filtersOpen}
        data-testid={`${testIdPrefix}-filter-toggle`}
      >
        <Funnel size={16} weight={filtersOpen ? "fill" : "regular"} />
        <span className="hidden sm:inline">Filtri</span>
        {filterCount > 0 && (
          <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-bold text-primary-foreground">{filterCount}</span>
        )}
      </button>
      {children}
    </div>
  );
}

export function ToolbarButton({ icon: Icon, label, onClick, testId, primary }) {
  return (
    <button
      onClick={onClick}
      title={label}
      className={`inline-flex h-10 shrink-0 items-center justify-center gap-2 rounded-md px-3 text-sm font-medium transition-colors ${primary ? "bg-primary font-semibold text-primary-foreground hover:bg-primary/90" : "border border-border bg-card text-foreground hover:bg-muted"}`}
      data-testid={testId}
    >
      {Icon && <Icon size={16} weight={primary ? "bold" : "regular"} />}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

export function FilterPanel({ open, onReset, onApply, canReset, testIdPrefix, children }) {
  if (!open) return null;
  return (
    <div className="rounded-lg border border-border bg-card print:hidden" data-testid={`${testIdPrefix}-filter-panel`}>
      <div className="grid grid-cols-2 gap-2 p-3 sm:grid-cols-3 lg:grid-cols-4">{children}</div>
      <div className="flex items-center justify-end gap-2 border-t border-border px-3 py-2.5">
        <button onClick={onReset} disabled={!canReset} className="inline-flex h-9 items-center rounded-md px-3 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-40" data-testid={`${testIdPrefix}-filter-reset`}>
          Poništi
        </button>
        <button onClick={onApply} className="inline-flex h-9 items-center rounded-md bg-primary px-4 text-sm font-semibold text-primary-foreground hover:bg-primary/90" data-testid={`${testIdPrefix}-filter-apply`}>
          Primijeni
        </button>
      </div>
    </div>
  );
}

export function FilterSelect({ value, onChange, options, testId, wide }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className={`rw-control ${wide ? "col-span-2" : ""}`} data-testid={testId}>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}

export function FilterDate({ value, onChange, placeholder, testId, minDate }) {
  return <DatePicker value={value} onChange={onChange} placeholder={placeholder} minDate={minDate} className="h-10 w-full" data-testid={testId} />;
}

export function ListSummaryRow({ left, right }) {
  return (
    <div className="flex items-center justify-between gap-3 px-1 text-xs text-muted-foreground print:hidden">
      <span className="truncate">{left}</span>
      {right && <span className="shrink-0 truncate">{right}</span>}
    </div>
  );
}
